/**
 * <TierCatalog> — admin subscription-tier catalog table.
 *
 * signup-billing-mvp Phase 5 Session 5 (2026-05-13).
 *
 * Renders the full tier catalog (active + inactive) from `GET /api/billing/admin/tiers`.
 * Hand-rolled `<table>` per research § 3 #17 + the `UserManagement.tsx`
 * precedent — Arcanum's 9 custom UI primitives don't include a Table.
 *
 * 4-state machine: loading (Skeleton rows) / error (banner) / empty
 * (EmptyState + "New tier" CTA) / success (table). Admin actions wired:
 *   - "New tier" → opens `<TierFormDialog mode="create">`
 *   - Edit → opens `<TierFormDialog mode="edit" tier={row}>`
 *   - Deactivate → opens `<ConfirmModal>` then POSTs `/deactivate`
 *
 * Stripe Product/Price IDs are READ-ONLY display (truncated) per spec § 5e —
 * system-managed; admin NEVER inputs them.
 */
import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Button, ConfirmModal, EmptyState, Skeleton } from '@/components/ui';
import { api } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';
import { TierFormDialog } from './TierFormDialog';

interface TierRow {
  id: string;
  display_name: string;
  tagline: string;
  monthly_price_usd: number | null;
  usage_multiplier: number;
  feature_bullets: string[];
  sort_order: number;
  is_active: boolean;
  version: number;
  stripe_product_id: string | null;
  stripe_price_id: string | null;
  derived_monthly_grant_cogs: number;
}

interface AdminTiersEnvelope {
  data: TierRow[];
}

function truncateStripeId(id: string | null): string {
  if (!id) return '—';
  if (id.length <= 14) return id;
  return `${id.slice(0, 10)}…${id.slice(-3)}`;
}

function formatPriceUsd(value: number | null): string {
  if (value === null) return 'Contact us';
  return `$${value.toLocaleString('en-US', { maximumFractionDigits: 2 })}`;
}

export function TierCatalog() {
  const queryClient = useQueryClient();
  const [createOpen, setCreateOpen] = useState(false);
  const [editTier, setEditTier] = useState<TierRow | null>(null);
  const [deactivateTier, setDeactivateTier] = useState<TierRow | null>(null);
  const [deactivateError, setDeactivateError] = useState<string | null>(null);

  const query = useQuery<TierRow[]>({
    queryKey: queryKeys.billing.adminTiers(),
    queryFn: async () => {
      const env = await api.get<AdminTiersEnvelope>('/api/billing/admin/tiers');
      return env.data;
    },
    staleTime: 30_000,
  });

  const deactivateMutation = useMutation({
    mutationFn: async (tierId: string) => {
      const env = await api.post<AdminTiersEnvelope>(
        `/api/billing/admin/tiers/${tierId}/deactivate`,
        {},
      );
      return env.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.billing.adminTiers() });
      setDeactivateTier(null);
      setDeactivateError(null);
    },
    onError: (err: unknown) => {
      const message =
        err instanceof Error ? err.message : 'Failed to deactivate tier';
      setDeactivateError(message);
    },
  });

  const isBaseTier = (row: TierRow): boolean => row.usage_multiplier === 1;

  return (
    <div className="flex flex-col gap-3" data-testid="tier-catalog">
      <div className="flex items-center justify-between">
        <span className="text-xs font-mono font-semibold text-text-muted tracking-[1.4px] uppercase">
          Tier Catalog
        </span>
        <Button variant="primary" onClick={() => setCreateOpen(true)}>
          New tier
        </Button>
      </div>

      {/* Loading state */}
      {query.isLoading && (
        <div className="flex flex-col gap-2" data-testid="tier-catalog-loading">
          <Skeleton variant="line" className="h-8 w-full" />
          <Skeleton variant="line" className="h-8 w-full" />
          <Skeleton variant="line" className="h-8 w-full" />
        </div>
      )}

      {/* Error state */}
      {query.isError && !query.isLoading && (
        <div
          className="px-3 py-2 bg-danger/[0.06] border border-danger/15 rounded text-danger text-xs font-mono"
          data-testid="tier-catalog-error"
        >
          Failed to load tier catalog.{' '}
          <button
            className="underline cursor-pointer"
            onClick={() => query.refetch()}
            type="button"
          >
            Retry
          </button>
        </div>
      )}

      {/* Empty state */}
      {query.isSuccess && query.data.length === 0 && (
        <EmptyState
          title="NO TIERS CONFIGURED"
          description="Create the first tier to start accepting subscriptions."
          action={
            <Button variant="primary" onClick={() => setCreateOpen(true)}>
              New tier
            </Button>
          }
        />
      )}

      {/* Success state — hand-rolled table (research § 3 #17). */}
      {query.isSuccess && query.data.length > 0 && (
        <div className="overflow-x-auto border border-panel-border rounded-[3px]">
          <table className="w-full text-xs font-mono" data-testid="tier-catalog-table">
            <thead>
              <tr className="border-b border-panel-border bg-panel-bg/40">
                <th className="text-left px-3 py-2 text-text-muted font-semibold tracking-[1.2px] uppercase">
                  ID
                </th>
                <th className="text-left px-3 py-2 text-text-muted font-semibold tracking-[1.2px] uppercase">
                  Display
                </th>
                <th className="text-left px-3 py-2 text-text-muted font-semibold tracking-[1.2px] uppercase">
                  Tagline
                </th>
                <th className="text-right px-3 py-2 text-text-muted font-semibold tracking-[1.2px] uppercase">
                  Price
                </th>
                <th className="text-right px-3 py-2 text-text-muted font-semibold tracking-[1.2px] uppercase">
                  Mult.
                </th>
                <th className="text-right px-3 py-2 text-text-muted font-semibold tracking-[1.2px] uppercase">
                  Grant
                </th>
                <th className="text-left px-3 py-2 text-text-muted font-semibold tracking-[1.2px] uppercase">
                  Stripe Product
                </th>
                <th className="text-left px-3 py-2 text-text-muted font-semibold tracking-[1.2px] uppercase">
                  Stripe Price
                </th>
                <th className="text-center px-3 py-2 text-text-muted font-semibold tracking-[1.2px] uppercase">
                  Active
                </th>
                <th className="text-right px-3 py-2 text-text-muted font-semibold tracking-[1.2px] uppercase">
                  Order
                </th>
                <th className="text-right px-3 py-2 text-text-muted font-semibold tracking-[1.2px] uppercase">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {query.data.map((row) => (
                <tr
                  key={row.id}
                  className="border-b border-panel-border/40 hover:bg-input-bg/40 transition-colors"
                  data-testid={`tier-row-${row.id}`}
                >
                  <td className="px-3 py-2 text-text-bright">{row.id}</td>
                  <td className="px-3 py-2 text-text-bright font-body">{row.display_name}</td>
                  <td className="px-3 py-2 text-text-muted font-body max-w-[160px] truncate">
                    {row.tagline}
                  </td>
                  <td className="px-3 py-2 text-right text-accent tabular-nums">
                    {formatPriceUsd(row.monthly_price_usd)}
                  </td>
                  <td className="px-3 py-2 text-right text-text tabular-nums">
                    {row.usage_multiplier}×
                  </td>
                  <td className="px-3 py-2 text-right text-text-muted tabular-nums">
                    {typeof row.derived_monthly_grant_cogs === 'number'
                      ? row.derived_monthly_grant_cogs.toLocaleString('en-US')
                      : '—'}
                  </td>
                  <td className="px-3 py-2 text-text-dim" title={row.stripe_product_id ?? ''}>
                    {truncateStripeId(row.stripe_product_id)}
                  </td>
                  <td className="px-3 py-2 text-text-dim" title={row.stripe_price_id ?? ''}>
                    {truncateStripeId(row.stripe_price_id)}
                  </td>
                  <td className="px-3 py-2 text-center">
                    <span
                      className={
                        row.is_active
                          ? 'text-accent text-micro tracking-[0.5px] font-semibold uppercase'
                          : 'text-text-dim text-micro tracking-[0.5px] font-semibold uppercase'
                      }
                    >
                      {row.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-right text-text-muted tabular-nums">
                    {row.sort_order}
                  </td>
                  <td className="px-3 py-2 text-right">
                    <div className="flex items-center justify-end gap-1.5">
                      <Button
                        variant="secondary"
                        onClick={() => setEditTier(row)}
                        data-testid={`tier-edit-${row.id}`}
                      >
                        Edit
                      </Button>
                      {row.is_active && (
                        <Button
                          variant="danger"
                          onClick={() => {
                            setDeactivateError(null);
                            setDeactivateTier(row);
                          }}
                          data-testid={`tier-deactivate-${row.id}`}
                        >
                          Deactivate
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Create dialog */}
      {createOpen && (
        <TierFormDialog
          mode="create"
          onClose={() => setCreateOpen(false)}
          onSaved={() => {
            queryClient.invalidateQueries({ queryKey: queryKeys.billing.adminTiers() });
            setCreateOpen(false);
          }}
        />
      )}

      {/* Edit dialog */}
      {editTier && (
        <TierFormDialog
          mode="edit"
          tier={editTier}
          onClose={() => setEditTier(null)}
          onSaved={() => {
            queryClient.invalidateQueries({ queryKey: queryKeys.billing.adminTiers() });
            setEditTier(null);
          }}
        />
      )}

      {/* Deactivate confirm */}
      <ConfirmModal
        open={!!deactivateTier}
        onClose={() => {
          setDeactivateTier(null);
          setDeactivateError(null);
        }}
        onConfirm={() => deactivateTier && deactivateMutation.mutate(deactivateTier.id)}
        title="Deactivate Tier"
        confirmLabel="Deactivate"
        danger
      >
        <div className="flex flex-col gap-2">
          <span>
            Deactivate tier <span className="font-mono text-text-bright">{deactivateTier?.id}</span>?
            New subscriptions will not be available; existing subscribers keep their access.
          </span>
          {deactivateTier && isBaseTier(deactivateTier) && (
            <span className="text-warn text-xs font-mono">
              Warning: this is the BASE tier (usage_multiplier = 1.0). The RPC will refuse to
              deactivate it unless another tier is first promoted to base.
            </span>
          )}
          {deactivateError && (
            <span className="text-danger text-xs font-mono" data-testid="tier-deactivate-error">
              {deactivateError}
            </span>
          )}
        </div>
      </ConfirmModal>
    </div>
  );
}
